import { RefreshIcon, XIcon } from "@heroicons/react/outline";
import * as React from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useOfflineTaskUploads } from "~/config/offline-storage/useOfflineTaskUploads.hook";
import { useOfflineUploadsCleanup } from "~/config/offline-storage/useOfflineUploads.hook";
import { Box } from "~/design-system/Box";
import { Button } from "~/design-system/Button";
import { Inline } from "~/design-system/Inline";
import { Stack } from "~/design-system/Stack";
import { Text } from "~/design-system/Text";
import { CompletedUploadsSection } from "./CompletedUploadsSection";
import { ErrorUploadsSection } from "./ErrorUploadsSection";
import { PendingUploadItem } from "./PendingUploadItem";
import { StorageIndicator } from "./StorageIndicator";
import { UploadingUploadItem } from "./UploadingUploadItem";
import { UploadsList } from "./UploadsList";

export const OfflineUploadsManager: React.FC = () => {
  const { t } = useTranslation();
  const { isProcessing, removeUpload, uploads } = useOfflineTaskUploads();

  // Initialize automatic cleanup for chat uploads
  useOfflineUploadsCleanup();

  const [isVisible, setIsVisible] = React.useState(true);
  const [lastCompletedCount, setLastCompletedCount] = React.useState(0);
  const [lastErrorCount, setLastErrorCount] = React.useState(0);
  const [expandedErrors, setExpandedErrors] = React.useState<Set<string>>(
    new Set()
  );

  const {
    completedUploads,
    errorUploads,
    maxStorage,
    pendingUploads,
    totalSize,
    uploadingUploads,
  } = React.useMemo(() => {
    const totalSize = uploads.reduce(
      (total, upload) => total + upload.fileSize,
      0
    );
    const maxStorage = 2 * 1024 * 1024 * 1024; // 2 GB

    const pendingUploads = uploads.filter((u) => u.status === "pending");
    const uploadingUploads = uploads.filter((u) => u.status === "uploading");
    const completedUploads = uploads.filter((u) => u.status === "complete");
    const errorUploads = uploads.filter((u) => u.status === "error");

    return {
      completedUploads,
      errorUploads,
      maxStorage,
      pendingUploads,
      totalSize,
      uploadingUploads,
    };
  }, [uploads]);

  React.useEffect(() => {
    const hasPendingOrUploading =
      pendingUploads.length > 0 || uploadingUploads.length > 0;

    if (!hasPendingOrUploading && uploads.length > 0) {
      const timer = setTimeout(() => {
        setIsVisible(false);
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [pendingUploads.length, uploadingUploads.length, uploads.length]);

  React.useEffect(() => {
    if (pendingUploads.length > 0 || uploadingUploads.length > 0) {
      setIsVisible(true);
    }
  }, [pendingUploads.length, uploadingUploads.length]);

  React.useEffect(() => {
    const currentCompletedCount = completedUploads.length;

    if (currentCompletedCount > lastCompletedCount) {
      const newCompletions = currentCompletedCount - lastCompletedCount;

      if (newCompletions > 0) {
        toast.success(
          t(
            "screens.ProjectTasksListScreen.UpdateTaskForm.offlineUploadSuccess",
            "{{count}} file(s) uploaded successfully",
            { count: newCompletions }
          )
        );
      }

      setLastCompletedCount(currentCompletedCount);
    }
  }, [completedUploads.length, lastCompletedCount, t]);

  React.useEffect(() => {
    const currentErrorCount = errorUploads.length;

    if (currentErrorCount > lastErrorCount) {
      const newErrors = currentErrorCount - lastErrorCount;

      if (newErrors > 0) {
        toast.error(
          t(
            "screens.ProjectTasksListScreen.UpdateTaskForm.offlineUploadError",
            "{{count}} file(s) failed to upload",
            { count: newErrors }
          )
        );
      }

      setLastErrorCount(currentErrorCount);
    }
  }, [errorUploads.length, lastErrorCount, t]);

  if (uploads.length === 0 || !isVisible) {
    return null;
  }

  const hasActiveUploads =
    pendingUploads.length > 0 || uploadingUploads.length > 0;
  if (!hasActiveUploads) {
    return null;
  }

  const toggleErrorExpansion = (uploadId: string) => {
    setExpandedErrors((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(uploadId)) {
        newSet.delete(uploadId);
      } else {
        newSet.add(uploadId);
      }
      return newSet;
    });
  };

  const handleRemoveErrorUpload = (upload: (typeof errorUploads)[0]) => {
    removeUpload(upload.id);
    toast.success(
      t(
        "screens.ProjectTasksListScreen.UpdateTaskForm.offlineUploadRemoved",
        "File removed from memory"
      )
    );
  };

  const handleRemovePendingUpload = (upload: (typeof pendingUploads)[0]) => {
    removeUpload(upload.id);
    toast.success(
      t(
        "screens.ProjectTasksListScreen.UpdateTaskForm.offlineUploadRemoved",
        "File removed from memory"
      )
    );
  };

  const handleRemoveUploadingUpload = (
    upload: (typeof uploadingUploads)[0]
  ) => {
    removeUpload(upload.id);
    toast.success(
      t(
        "screens.ProjectTasksListScreen.UpdateTaskForm.offlineUploadRemoved",
        "File removed from memory"
      )
    );
  };

  return (
    <Box
      css={{
        backgroundColor: "$white",
        border: "1px solid $neutral300",
        borderRadius: "$medium",
        bottom: "$large",
        boxShadow: "$large",
        left: "50%",
        maxWidth: "400px",
        minWidth: "300px",
        opacity: isVisible ? 1 : 0,
        padding: "$medium",
        pointerEvents: isVisible ? "auto" : "none",
        position: "fixed",
        transform: isVisible
          ? "translateX(-50%) translateY(0)"
          : "translateX(-50%) translateY(10px)",
        transition: "opacity 0.3s ease, transform 0.3s ease",
        zIndex: 1000,
      }}
    >
      <Stack space="small">
        <Box
          css={{
            alignItems: "center",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Inline space="xsmall" alignY="center">
            <Text size="small" weight="strong">
              {t("Offline uploads")}
            </Text>
            {isProcessing && (
              <RefreshIcon
                height={16}
                width={16}
                style={{ animation: "spin 1s linear infinite" }}
              />
            )}
          </Inline>
          <Button
            size="xsmall"
            variant="ghost"
            circular
            onClick={() => setIsVisible(false)}
            iconLabel="Close"
          >
            <XIcon height={12} width={12} />
          </Button>
        </Box>

        <StorageIndicator totalSize={totalSize} maxStorage={maxStorage} />

        <UploadsList
          uploads={pendingUploads}
          title={t("Pending")}
          titleTone="caution"
        >
          {(upload) => (
            <PendingUploadItem
              key={upload.id}
              upload={upload}
              onRemove={handleRemovePendingUpload}
            />
          )}
        </UploadsList>

        <UploadsList
          uploads={uploadingUploads}
          title={t("Uploading")}
          titleTone="brandAccent"
        >
          {(upload) => (
            <UploadingUploadItem
              key={upload.id}
              upload={upload}
              onRemove={handleRemoveUploadingUpload}
            />
          )}
        </UploadsList>

        <CompletedUploadsSection completedUploads={completedUploads} />

        <ErrorUploadsSection
          errorUploads={errorUploads}
          expandedErrors={expandedErrors}
          onToggleExpansion={toggleErrorExpansion}
          onRemove={handleRemoveErrorUpload}
        />
      </Stack>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </Box>
  );
};
